/**
 * The Find picker: groups and people in one searchable list (design hand-off
 * "Find page — who am I finding", 2026-09-14).
 *
 * Groups are discrete cards with a SQUARE mark — mutually exclusive choices.
 * People are a continuous hairline list with round avatars. The subscribed
 * group shows a check, never a label. Liveness ("180 ft · 1 min", "Not
 * sharing") is shown only for members of the subscribed group; a member of
 * any other group is a plain row, because no sharing data exists for them.
 *
 * Picking a person in the subscribed group is EMPHASIS, never a filter.
 * Picking a person in another group moves the live subscription, which is a
 * BLE session restart — so it takes a confirm step that names both groups.
 */
import { useEffect, useMemo, useState } from 'react';
import Avatar from '../Avatar';
import Button from '../ui/Button';
import Modal, { ModalBody } from '../ui/Modal';
import SearchField from '../search/SearchField';
import { groupPrimaryLabel } from '../../lib/proximity/runtime';

const ACCENT_TINT = {
  background: 'rgb(125 59 168 / .15)',
  boxShadow: 'inset 0 0 0 1px rgb(125 59 168 / .3)',
};

function Check() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9a49d2"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** The matched substring tinted inside an otherwise plain name. */
export function Highlight({ text, query }) {
  const q = query.trim().toLowerCase();
  const i = q ? String(text).toLowerCase().indexOf(q) : -1;
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <span className="text-[#c9a3e6]">{text.slice(i, i + q.length)}</span>
      {text.slice(i + q.length)}
    </>
  );
}

/** The group's picture is its festival's (operator, 2026-09-23); the initial is the fallback. */
function GroupMark({ name, imageUrl }) {
  const [failed, setFailed] = useState(false);
  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-10 w-10 shrink-0 rounded-xl object-cover"
      />
    );
  }
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[rgb(125_59_168/.35)] text-sm font-semibold text-[#f1f5f9]"
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

const SECTION = 'mb-2 mt-[18px] text-micro font-semibold uppercase tracking-[.8px] text-[#94a3b8]';

export default function FindWhoPicker({
  isOpen,
  onClose,
  groups, // UserGroupOption[] (every group of the user)
  members, // [{ groupId, userId, displayName, photoUrl }] across groups, self excluded
  selectedGroupId,
  /** The session's groups (2026-10-12): everyone in any of them can be found now; no group to switch to. */
  sessionGroupIds = null,
  peerReadout, // (userId) => string | null   — liveness for the SUBSCRIBED group only
  onPickGroup, // (groupId) => void
  onPickPerson, // (userId) => void            — emphasis, same group
  onMoveToGroup, // (groupId, userId) => void   — explicit subscription move
}) {
  const [query, setQuery] = useState('');
  const [pendingMove, setPendingMove] = useState(null); // { group, person }
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setPendingMove(null);
    }
  }, [isOpen]);

  const q = query.trim().toLowerCase();
  const inSession = (groupId) =>
    sessionGroupIds ? sessionGroupIds.includes(groupId) : groupId === selectedGroupId;
  const groupById = useMemo(() => new Map((groups ?? []).map((g) => [g.id, g])), [groups]);
  const groupRows = useMemo(
    () => (groups ?? []).filter((g) => !q || groupPrimaryLabel(g).toLowerCase().includes(q)),
    [groups, q],
  );
  const peopleRows = useMemo(() => {
    const rows = (members ?? []).filter((m) => !q || m.displayName.toLowerCase().includes(q));
    // The subscribed group's people first (they can be found now), then the rest, each alphabetical.
    rows.sort((a, b) => {
      const sa = inSession(a.groupId) ? 0 : 1;
      const sb = inSession(b.groupId) ? 0 : 1;
      return sa - sb || a.displayName.localeCompare(b.displayName);
    });
    // A friend in two of your groups is one person (operator, 2026-09-23: "the same people multiple times").
    // After the sort, the first row kept is the one in the subscribed group, the one that can be found now.
    const seen = new Set();
    return rows.filter((m) => (seen.has(m.userId) ? false : (seen.add(m.userId), true)));
  }, [members, q, selectedGroupId, sessionGroupIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const memberCount = (groupId) => (members ?? []).filter((m) => m.groupId === groupId).length + 1; // + you

  const tapPerson = (m) => {
    if (inSession(m.groupId)) {
      onPickPerson?.(m.userId);
      return;
    }
    setPendingMove({ group: groupById.get(m.groupId), person: m });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Search" size="md">
      <ModalBody className="max-h-[70vh] overflow-y-auto pt-0">
        {pendingMove ? (
          <div className="py-2">
            <p className="text-body font-semibold text-[#f1f5f9]">
              Switch to {groupPrimaryLabel(pendingMove.group)}?
            </p>
            <p className="mt-2 text-meta text-[#94a3b8]">
              {pendingMove.person.displayName} is in a different group. Finding them means leaving{' '}
              {groupById.get(selectedGroupId)
                ? groupPrimaryLabel(groupById.get(selectedGroupId))
                : 'your current group'}{' '}
              and joining {groupPrimaryLabel(pendingMove.group)} on the radar.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingMove(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const { group, person } = pendingMove;
                  setPendingMove(null);
                  onMoveToGroup?.(group.id, person.userId);
                }}
              >
                Switch
              </Button>
            </div>
          </div>
        ) : (
          <>
            <SearchField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery('')}
              placeholder={sessionGroupIds ? 'People' : 'Groups and people'}
              aria-label={sessionGroupIds ? 'Search people' : 'Search groups and people'}
            />

            {(!q || groupRows.length > 0) && groupRows.length > 0 && (
              <>
                <h3 className={SECTION}>Groups</h3>
                <ul className="flex flex-col gap-1">
                  {groupRows.map((g) => {
                    const on = g.id === selectedGroupId;
                    return (
                      <li key={g.id}>
                        <button
                          type="button"
                          onClick={() => onPickGroup?.(g.id)}
                          className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-[10px] py-2 text-left"
                          style={on ? ACCENT_TINT : undefined}
                          aria-pressed={on}
                        >
                          <GroupMark name={groupPrimaryLabel(g)} imageUrl={g.eventImageUrl} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-label font-semibold text-[#f1f5f9]">
                              <Highlight text={groupPrimaryLabel(g)} query={query} />
                            </span>
                            <span className="block truncate text-meta text-[#94a3b8]">
                              {memberCount(g.id)} {memberCount(g.id) === 1 ? 'person' : 'people'}
                              {g.eventName ? ` · ${g.eventName}` : ''}
                            </span>
                          </span>
                          {on && (
                            <span className="grid h-[22px] w-[22px] shrink-0 place-items-center">
                              <Check />
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {peopleRows.length > 0 && (
              <>
                <h3 className={`${SECTION} mb-1`}>People</h3>
                <ul className="divide-y divide-[rgba(51,65,85,0.6)]">
                  {peopleRows.map((m) => {
                    const inGroup = inSession(m.groupId);
                    const readout = inGroup ? (peerReadout?.(m.userId) ?? 'Not sharing') : null;
                    const dim = inGroup && readout === 'Not sharing';
                    return (
                      <li key={`${m.groupId}:${m.userId}`}>
                        <button
                          type="button"
                          onClick={() => tapPerson(m)}
                          className={`flex min-h-[44px] w-full items-center gap-3 px-[10px] py-[10px] text-left ${dim ? 'opacity-[.55]' : ''}`}
                        >
                          <Avatar
                            displayName={m.displayName}
                            profilePhotoUrl={m.photoUrl}
                            size="sm"
                          />
                          <span className="min-w-0 flex-1 truncate text-label font-semibold text-[#f1f5f9]">
                            <Highlight text={m.displayName} query={query} />
                          </span>
                          {readout && (
                            <span className="shrink-0 text-meta font-medium tabular-nums text-[#94a3b8]">
                              {readout}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {q && groupRows.length === 0 && peopleRows.length === 0 && (
              <p className="py-6 text-center text-meta text-[#94a3b8]">
                No groups or people match.
              </p>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
